<template>
  <div class="pa-tc">
    <div class="pa-tc__title">
      <i class="material-icons" aria-hidden="true">gavel</i>
      <span>Website Publishing Agreement</span>
    </div>
    <ul class="pa-tc__list">
      <li v-for="bullet in bullets" :key="bullet">{{ bullet }}</li>
    </ul>
    <button type="button" class="pa-tc__link" @click="expanded = !expanded">
      {{ expanded ? 'Hide the full agreement' : 'Read the full Website Publishing Agreement' }}
    </button>
    <publishing-agreement-text
      v-if="expanded"
      :client-name="siteName"
      :website-url="siteUrl"
      :effective-date="signedAt"
    />

    <div class="pa-tc__ack">
      <div
        class="pa-check"
        :class="{ 'pa-check--sel': value, 'pa-check--locked': locked }"
        role="checkbox"
        :aria-checked="value ? 'true' : 'false'"
        :aria-disabled="locked ? 'true' : 'false'"
        :tabindex="locked ? -1 : 0"
        @click="toggle"
        @keydown.space.prevent="toggle"
        @keydown.enter.prevent="toggle"
      >
        <span class="pa-check__box"><i v-if="value" class="material-icons">check</i></span>
        <span>
          {{ ackText }}
          <span v-if="signedAt" class="pa-tc__signed">Signed on {{ signedDateLabel }}.</span>
        </span>
      </div>
    </div>

    <div class="pa-tc__contact">
      <button type="button" class="pa-tc__link" @click="$emit('contact')">
        Have a question about the agreement, or specific requirements? Contact your SEO Manager
      </button>
    </div>
  </div>
</template>

<script>
import PublishingAgreementText from '@/components/SitesSingleView/PopAuthority/PublishingAgreementText.vue'
import {
  AGREEMENT_ACK_TEXT,
  AGREEMENT_SETUP_BULLETS,
  formatAgreementDate,
} from '@/utils/popAuthorityAgreement'

/**
 * Website Publishing Agreement box of the publishing step (design pa-setup.jsx PublishingStep
 * `.pa-tc`): shown when the client chose "Yes, please publish for us". `value` is the acceptance
 * checkbox (a form answer, `step2.publishing_agreement_accepted`); once the site has signed the
 * current agreement (`signedAt`) the box stays checked and locked. "Contact your SEO Manager"
 * emits `contact`; the step opens the "Message your SEO Manager" box.
 */
export default {
  name: 'PopAuthorityPublishingAgreementBox',
  components: { PublishingAgreementText },
  props: {
    value: { type: Boolean, default: false },
    /** Backend timestamp of the site's signature of the current agreement version, or ''. */
    signedAt: { type: String, default: '' },
    siteName: { type: String, default: '' },
    siteUrl: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
  },
  data () {
    return {
      expanded: false,
      bullets: AGREEMENT_SETUP_BULLETS,
      ackText: AGREEMENT_ACK_TEXT,
    }
  },
  computed: {
    locked () {
      return this.readonly || Boolean(this.signedAt)
    },
    signedDateLabel () {
      return formatAgreementDate(this.signedAt)
    },
  },
  watch: {
    signedAt: {
      handler (signedAt) {
        // an already signed site keeps the answer in sync with the signature
        if (signedAt && !this.value) this.$emit('input', true)
      },
      immediate: true,
    },
  },
  methods: {
    toggle () {
      if (this.locked) return
      this.$emit('input', !this.value)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup.scss';

/* design pa.css .pa-tc: 1.5px #FFDFC2 border, #FFF7F0 background, 10px radius, 14px 16px padding
   (the form field wrapper already provides the 18px above) */
.pa-tc {
  border: 1.5px solid #ffdfc2;
  background: #fff7f0;
  border-radius: 10px;
  padding: 14px 16px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1.45;
    margin-bottom: 8px;

    .material-icons {
      color: $pa-accent;
      font-size: 19px;
    }
  }

  &__list {
    margin: 0 0 10px;
    padding-left: 20px;
    list-style: disc;
    color: $pa-muted;
    font-size: 0.88rem;
    line-height: 1.45;

    li {
      margin-bottom: 4px;
      font-size: 0.88rem;
      line-height: 1.45;
      color: $pa-muted;
    }
  }

  &__link {
    display: inline;
    background: none;
    border: none;
    padding: 0;
    color: $pa-link;
    font-weight: 700;
    font-size: 0.85rem;
    line-height: 1.45;
    cursor: pointer;
    font-family: inherit;
    text-align: left;

    &:hover {
      text-decoration: underline;
    }
  }

  &__ack {
    margin-top: 12px;
  }

  &__signed {
    color: $pa-muted;
    font-weight: 600;
  }

  &__contact {
    margin-top: 10px;
    font-size: 0.85rem;
    line-height: 1.45;
  }
}

.pa-check--locked {
  cursor: default;
}
</style>
